import { FC, useEffect, useState } from 'react';
import {
  Avatar,
  Grid,
  GridItem,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';

import {
  Role,
  TreatyDetailsFragment,
  TreatyUserDetailFragment,
  TreatyUserFragment,
  useGetTreatyUserQuery,
  UserType,
} from '../../graphql/generated';

import { formatTimestamp } from '../../utils/date/date';
import { EditableEmailNotifications } from './components/editable-email-notifications/editable-email-notifications';
import { EditableUserRoles } from './components/editable-user-roles/editable-user-roles';
import { MFA } from './components/mfa/mfa';

type UserDetailsModalProps = {
  user: TreatyUserFragment;
  roles: Role[];
  treaties: TreatyDetailsFragment[];
  isOpen: boolean;
  onClose: () => void;
};

export const UserDetailsModal: FC<UserDetailsModalProps> = ({
  user,
  roles,
  treaties,
  isOpen,
  onClose,
}) => {
  const [treatyUser, setTreatyUser] = useState<TreatyUserDetailFragment>({
    ...user,
    // Placeholder until we get the real value - we only query for this value during modal open (i.e. only when needed) because it is rate-limited by the Auth0 API
    hasMFAEnabled: false,
  });

  const { data, loading, startPolling, stopPolling } = useGetTreatyUserQuery({
    variables: { id: user.id },
    fetchPolicy: 'network-only',
    skip: !isOpen,
  });

  useEffect(() => {
    if (data?.treatyUser) {
      setTreatyUser(data.treatyUser);
    }
  }, [data]);

  const isExternalUser = treatyUser.type === UserType.EXTERNAL_USER;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent data-testid="user-details-modal">
        <ModalHeader px={8} py={6}>
          <HStack spacing={4}>
            <Avatar name={treatyUser.name} bg="blue.100" color="blue.400" />
            <Text fontSize="2xl" display="flex">
              {user.name}
              {user.isBlocked ? (
                <Tag
                  size="md"
                  marginLeft="3"
                  colorScheme="red"
                  alignSelf="center"
                >
                  Blocked
                </Tag>
              ) : null}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={8} pb={8}>
          <Grid
            templateColumns="repeat(2, 1fr)"
            gap={4}
            p={6}
            layerStyle="border"
          >
            <LabelAndValue label="Name" value={treatyUser.name} />
            <LabelAndValue label="Email" value={treatyUser.email} />
            <LabelAndValue
              label="Last Login"
              value={formatTimestampNullable(treatyUser.lastLogin)}
            />
            <LabelAndValue
              label="Created At"
              value={formatTimestampNullable(treatyUser.createdAt)}
            />
          </Grid>

          <EditableUserRoles
            startPolling={startPolling}
            stopPolling={stopPolling}
            treatyUser={treatyUser}
            roles={roles}
            canEdit={isExternalUser}
          />

          <EditableEmailNotifications
            treatyUser={treatyUser}
            treaties={treaties}
          />

          {isExternalUser && (
            <MFA treatyUser={treatyUser} isLoading={loading} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const LabelAndValue: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  return (
    <Stack as={GridItem} spacing={1}>
      <Text fontWeight="semibold" color="gray.500">
        {label}
      </Text>
      <Text>{value || '--'}</Text>
    </Stack>
  );
};

const formatTimestampNullable = (date?: string | null): string => {
  return date ? formatTimestamp(date) : '';
};
